// Preset & Snapshot storage — IndexedDB layer
// Separate DB to avoid version conflicts with main verigit DB

import { openDB, type IDBPDatabase } from 'idb';
import { compress as poolCompress, decompress as poolDecompress } from './compression-pool';
import type {
  Preset,
  SnapshotMeta,
  SnapshotBlob,
  SnapshotData,
  HydratedSnapshot,
} from './preset-types';
import { ENGINE_VERSION } from './preset-types';

const DB_NAME = 'verigit-presets';
const DB_VERSION = 1;

const STORES = {
  PRESETS: 'presets',
  SNAPSHOT_META: 'snapshot_meta',
  SNAPSHOT_BLOBS: 'snapshot_blobs',
} as const;

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDB(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORES.PRESETS)) {
          const store = db.createObjectStore(STORES.PRESETS, { keyPath: 'id' });
          store.createIndex('sortOrder', 'sortOrder');
          store.createIndex('updatedAt', 'updatedAt');
        }
        if (!db.objectStoreNames.contains(STORES.SNAPSHOT_META)) {
          const store = db.createObjectStore(STORES.SNAPSHOT_META, { keyPath: 'id' });
          store.createIndex('presetId', 'presetId');
          store.createIndex('analysedAt', 'analysedAt');
        }
        if (!db.objectStoreNames.contains(STORES.SNAPSHOT_BLOBS)) {
          db.createObjectStore(STORES.SNAPSHOT_BLOBS, { keyPath: 'id' });
        }
      },
    });
  }
  return dbPromise;
}

// ==================== Presets CRUD ====================

export async function getAllPresets(): Promise<Preset[]> {
  const db = await getDB();
  const all = await db.getAll(STORES.PRESETS);
  return all.sort((a, b) => {
    // Pinned first, then by sortOrder
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    return a.sortOrder - b.sortOrder;
  });
}

export async function getPreset(id: string): Promise<Preset | undefined> {
  const db = await getDB();
  return db.get(STORES.PRESETS, id);
}

export async function savePreset(preset: Preset): Promise<void> {
  const db = await getDB();
  preset.updatedAt = new Date().toISOString();
  await db.put(STORES.PRESETS, preset);
}

export async function deletePreset(id: string): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.PRESETS, id);
  // Also delete associated snapshots
  await deleteSnapshotsForPreset(id);
}

export async function getNextSortOrder(): Promise<number> {
  const presets = await getAllPresets();
  if (presets.length === 0) return 0;
  return Math.max(...presets.map(p => p.sortOrder)) + 1;
}

// ==================== Snapshot Meta ====================

export async function getSnapshotMetaForPreset(presetId: string): Promise<SnapshotMeta | undefined> {
  const db = await getDB();
  const all = await db.getAllFromIndex(STORES.SNAPSHOT_META, 'presetId', presetId);
  // Return the most recent valid snapshot
  const valid = all
    .filter(s => s.engineVersion === ENGINE_VERSION)
    .sort((a, b) => new Date(b.analysedAt).getTime() - new Date(a.analysedAt).getTime());
  return valid[0];
}

export async function getAllSnapshotMetas(): Promise<SnapshotMeta[]> {
  const db = await getDB();
  return db.getAll(STORES.SNAPSHOT_META);
}

// ==================== Snapshot Write ====================

export async function saveSnapshot(
  meta: SnapshotMeta,
  data: SnapshotData
): Promise<void> {
  const db = await getDB();

  // Compress the data (off main thread via worker pool)
  const jsonStr = JSON.stringify(data);
  const { compressed, originalSize, compressedSize } = await poolCompress(jsonStr);

  meta.uncompressedSize = originalSize;
  meta.compressedSize = compressedSize;
  meta.engineVersion = ENGINE_VERSION;

  const blob: SnapshotBlob = {
    id: meta.id,
    presetId: meta.presetId,
    compressedData: compressed as string,
  };

  // Write both in a single transaction
  const tx = db.transaction([STORES.SNAPSHOT_META, STORES.SNAPSHOT_BLOBS], 'readwrite');
  await Promise.all([
    tx.objectStore(STORES.SNAPSHOT_META).put(meta),
    tx.objectStore(STORES.SNAPSHOT_BLOBS).put(blob),
    tx.done,
  ]);
}

// ==================== Snapshot Read (Hydrate) ====================

export async function hydrateSnapshot(presetId: string): Promise<HydratedSnapshot | null> {
  const meta = await getSnapshotMetaForPreset(presetId);
  if (!meta) return null;

  const db = await getDB();
  const blob: SnapshotBlob | undefined = await db.get(STORES.SNAPSHOT_BLOBS, meta.id);
  if (!blob) return null;

  try {
    const jsonStr = await poolDecompress(blob.compressedData);
    const data: SnapshotData = JSON.parse(jsonStr);
    return {
      meta,
      data,
      hydratedAt: Date.now(),
    };
  } catch (err) {
    console.error('[preset-store] Failed to hydrate snapshot:', err);
    return null;
  }
}

// ==================== Snapshot Cleanup ====================

async function deleteSnapshotsForPreset(presetId: string): Promise<void> {
  const db = await getDB();
  const metas = await db.getAllFromIndex(STORES.SNAPSHOT_META, 'presetId', presetId);
  const tx = db.transaction([STORES.SNAPSHOT_META, STORES.SNAPSHOT_BLOBS], 'readwrite');
  for (const meta of metas) {
    tx.objectStore(STORES.SNAPSHOT_META).delete(meta.id);
    tx.objectStore(STORES.SNAPSHOT_BLOBS).delete(meta.id);
  }
  await tx.done;
}

export async function pruneOldSnapshots(maxPerPreset: number = 3): Promise<number> {
  const db = await getDB();
  const allMetas = await db.getAll(STORES.SNAPSHOT_META);

  // Group by presetId
  const byPreset = new Map<string, SnapshotMeta[]>();
  for (const m of allMetas) {
    const arr = byPreset.get(m.presetId) || [];
    arr.push(m);
    byPreset.set(m.presetId, arr);
  }

  let pruned = 0;
  const tx = db.transaction([STORES.SNAPSHOT_META, STORES.SNAPSHOT_BLOBS], 'readwrite');

  for (const [, metas] of byPreset) {
    if (metas.length <= maxPerPreset) continue;
    // Sort newest first, delete the rest
    metas.sort((a, b) => new Date(b.analysedAt).getTime() - new Date(a.analysedAt).getTime());
    for (let i = maxPerPreset; i < metas.length; i++) {
      tx.objectStore(STORES.SNAPSHOT_META).delete(metas[i].id);
      tx.objectStore(STORES.SNAPSHOT_BLOBS).delete(metas[i].id);
      pruned++;
    }
  }

  await tx.done;
  return pruned;
}

// ==================== Hot Cache (In-Memory LRU) ====================

const MAX_HOT_ITEMS = 5;
const hotCache = new Map<string, HydratedSnapshot>();

export function getFromHotCache(presetId: string): HydratedSnapshot | undefined {
  const item = hotCache.get(presetId);
  if (item) {
    // Touch LRU timestamp
    item.hydratedAt = Date.now();
  }
  return item;
}

export function putInHotCache(snapshot: HydratedSnapshot): void {
  snapshot.hydratedAt = Date.now();
  hotCache.set(snapshot.meta.presetId, snapshot);

  // Evict LRU if over limit
  if (hotCache.size > MAX_HOT_ITEMS) {
    let oldestKey = '';
    let oldestTime = Infinity;
    for (const [key, val] of hotCache) {
      if (val.hydratedAt < oldestTime) {
        oldestTime = val.hydratedAt;
        oldestKey = key;
      }
    }
    if (oldestKey) hotCache.delete(oldestKey);
  }
}

export function removeFromHotCache(presetId: string): void {
  hotCache.delete(presetId);
}

export function clearHotCache(): void {
  hotCache.clear();
}

export function getHotCacheKeys(): string[] {
  return Array.from(hotCache.keys());
}

// ==================== Quick Load (Hot → Warm → null) ====================

// ==================== Bulk Export / Import (for profile transfer) ====================

export interface PresetStoreExport {
  presets: Preset[];
  snapshotMetas: SnapshotMeta[];
  snapshotBlobs: SnapshotBlob[];
}

export async function exportAll(): Promise<PresetStoreExport> {
  const db = await getDB();
  const [presets, snapshotMetas, snapshotBlobs] = await Promise.all([
    db.getAll(STORES.PRESETS),
    db.getAll(STORES.SNAPSHOT_META),
    db.getAll(STORES.SNAPSHOT_BLOBS),
  ]);
  return { presets, snapshotMetas, snapshotBlobs };
}

export async function importAll(data: PresetStoreExport): Promise<{ presets: number; snapshots: number }> {
  const db = await getDB();
  let presetCount = 0;
  let snapshotCount = 0;

  // Import presets
  if (data.presets?.length) {
    const tx = db.transaction(STORES.PRESETS, 'readwrite');
    for (const preset of data.presets) {
      await tx.store.put(preset);
      presetCount++;
    }
    await tx.done;
  }

  // Import snapshot metas + blobs
  if (data.snapshotMetas?.length || data.snapshotBlobs?.length) {
    const tx = db.transaction([STORES.SNAPSHOT_META, STORES.SNAPSHOT_BLOBS], 'readwrite');
    for (const meta of data.snapshotMetas || []) {
      await tx.objectStore(STORES.SNAPSHOT_META).put(meta);
      snapshotCount++;
    }
    for (const blob of data.snapshotBlobs || []) {
      await tx.objectStore(STORES.SNAPSHOT_BLOBS).put(blob);
    }
    await tx.done;
  }

  clearHotCache();
  return { presets: presetCount, snapshots: snapshotCount };
}

// ==================== Quick Load (Hot → Warm → null) ====================

export async function loadSnapshot(presetId: string): Promise<HydratedSnapshot | null> {
  // Try hot cache first
  const hot = getFromHotCache(presetId);
  if (hot) return hot;

  // Try warm (IndexedDB)
  const hydrated = await hydrateSnapshot(presetId);
  if (hydrated) {
    putInHotCache(hydrated);
    return hydrated;
  }

  return null;
}
